import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useLocation } from 'react-router-dom';
import Navigation from './Navigation';
import Sidebar from './Sidebar';
import SignalsSidebar from './context/SignalsSidebar';
import Footer from './Footer';
import GracePeriodBanner from './common/GracePeriodBanner';
import TrialWarningBanner from './common/TrialWarningBanner';
import ProjectBrainDialog from './context/ProjectBrainDialog';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentProject } = useProjects();
  const { isRightSidebarCollapsed } = useSidebar();
  const location = useLocation();

  const authPaths = ['/signin', '/register', '/forgot-password', '/reset-password', '/change-password', '/admin/login'];
  const isAuthPage = authPaths.some(path => location.pathname.startsWith(path));
  const isLandingPage = location.pathname === '/' || location.pathname === '/pricing' || location.pathname === '/coming-soon' || location.pathname === '/about-us' || location.pathname === '/features' || location.pathname === '/who-is-this-for';
  const isTermsPage = location.pathname === '/terms';
  const isSelectPlanPage = location.pathname === '/select-plan';
  const isPlanConfirmationPage = location.pathname === '/plan-confirmation';
  const isDashboardPage = location.pathname === '/dashboard';
  const isAdminPage = location.pathname.startsWith('/admin');
  const isOnboardingPage = location.pathname === '/onboarding';
  const isHomePage = location.pathname === '/home';
  const isChatConversationPage = location.pathname.startsWith('/chat/');
  const showSidebar = currentUser && !isAuthPage && !isLandingPage && !isTermsPage && !isSelectPlanPage && !isPlanConfirmationPage && !isAdminPage;
  const showRightSidebar = currentUser && (
    isOnboardingPage ||
    (isHomePage && !!currentProject) ||
    (isChatConversationPage && !!currentProject)
  );
  const showNavigation = !isAuthPage && !isLandingPage;
  const showFooter = isTermsPage || isSelectPlanPage || isPlanConfirmationPage || isDashboardPage;

  useEffect(() => {
    if (isAuthPage) {
      document.body.classList.add('auth-page');
    } else {
      document.body.classList.remove('auth-page');
    }

    return () => {
      document.body.classList.remove('auth-page');
    };
  }, [isAuthPage]);

  return (
    <div className="App">
      {currentUser && !isAuthPage && !isLandingPage && <GracePeriodBanner />}
      {currentUser && !isAuthPage && !isLandingPage && <TrialWarningBanner />}
      {showNavigation && <Navigation />}
      <div className={showSidebar ? 'app-container' : ''}>
        {showSidebar && <Sidebar />}
        <main className={[
          showSidebar ? 'main-content' : '',
          showRightSidebar && !isRightSidebarCollapsed ? '' : showSidebar ? 'no-right-sidebar' : '',
        ].filter(Boolean).join(' ')}>
          {children}
        </main>
        {showRightSidebar && <SignalsSidebar />}
      </div>
      {showFooter && <Footer />}
      {currentUser && <ProjectBrainDialog />}
    </div>
  );
};

export default AppLayout;